'use client';

import { driver } from 'driver.js';
import { useEffect, useRef } from 'react';

import 'driver.js/dist/driver.css';

import { useDoneTour } from '@/components/shared/components/auto-tour/services/hooks';

import { pageLevelLocalization } from '@/constant/localization';

interface TourStep {
  element: string;
  title?: string;
  icon?: string | { id: string; name: string; url?: string; [key: string]: any }; // URL of the icon image or icon object
  description: string;
  position?: 'top' | 'bottom' | 'left' | 'right' | 'auto';
  tabId?: string;
}

interface UseTourOptions {
  id?: string;
  steps: TourStep[];
  condition?: boolean;
  options?: any;
  delay?: number;
  onComplete?: () => void; // Callback when tour is completed
  onDestroy?: () => void; // Callback when tour is destroyed (completed or closed)
}

export function useTour({
  id,
  steps,
  condition = true,
  options,
  delay = 0,
  onComplete,
  onDestroy,
}: UseTourOptions) {
  const driverRef = useRef<ReturnType<typeof driver> | null>(null);
  const scrollPositionRef = useRef<number>(0);
  const { mutate } = useDoneTour();
  const { product: productLocalization } = pageLevelLocalization;

  // Scroll lock utilities for Safari mobile and desktop
  const lockScroll = () => {
    if (typeof window === 'undefined') return;

    // Save current scroll position
    scrollPositionRef.current = window.pageYOffset || document.documentElement.scrollTop;

    // Prevent touch move on document (for mobile)
    const preventTouchMove = (e: TouchEvent) => {
      // Allow scrolling inside driver popover
      const target = e.target as HTMLElement;
      if (target?.closest('.driver-popover')) {
        return;
      }
      e.preventDefault();
    };

    // Prevent wheel scrolling (for desktop)
    const preventWheel = (e: WheelEvent) => {
      // Allow scrolling inside driver popover
      const target = e.target as HTMLElement;
      if (target?.closest('.driver-popover')) {
        return;
      }
      e.preventDefault();
    };

    // Prevent keyboard scrolling (arrow keys, page up/down, space)
    const preventKeyScroll = (e: KeyboardEvent) => {
      const keys = [
        'ArrowUp',
        'ArrowDown',
        'ArrowLeft',
        'ArrowRight',
        'PageUp',
        'PageDown',
        'Home',
        'End',
        ' ',
      ];
      if (keys.includes(e.key)) {
        // Allow if inside popover
        const target = e.target as HTMLElement;
        if (target?.closest('.driver-popover')) {
          return;
        }
        e.preventDefault();
      }
    };

    document.addEventListener('touchmove', preventTouchMove, { passive: false });
    document.addEventListener('wheel', preventWheel, { passive: false });
    document.addEventListener('keydown', preventKeyScroll, { passive: false });

    (window as any).__preventTouchMove = preventTouchMove;
    (window as any).__preventWheel = preventWheel;
    (window as any).__preventKeyScroll = preventKeyScroll;
  };

  const unlockScroll = () => {
    if (typeof window === 'undefined') return;

    // Remove all event listeners
    const preventTouchMove = (window as any).__preventTouchMove;
    const preventWheel = (window as any).__preventWheel;
    const preventKeyScroll = (window as any).__preventKeyScroll;

    if (preventTouchMove) {
      document.removeEventListener('touchmove', preventTouchMove);
      delete (window as any).__preventTouchMove;
    }

    if (preventWheel) {
      document.removeEventListener('wheel', preventWheel);
      delete (window as any).__preventWheel;
    }

    if (preventKeyScroll) {
      document.removeEventListener('keydown', preventKeyScroll);
      delete (window as any).__preventKeyScroll;
    }
  };

  const showPopover = (popover: Element, adjustArrow?: () => void) => {
    try {
      const el = popover as HTMLElement;
      const activeElement = document.querySelector('.driver-active-element') as HTMLElement;

      // Reset animation state completely for popover and highlight to prevent jump/flicker
      // Note: We don't touch overlay - it should stay visible and animate smoothly
      el.style.transition = 'none';
      el.style.opacity = '0';

      if (activeElement) {
        activeElement.style.transition = 'none';
      }

      // Force reflow
      el.offsetHeight;
      if (activeElement) activeElement.offsetHeight;

      // Reset any manual hiding styles to let driver.js handle animation
      el.style.removeProperty('opacity');
      el.style.removeProperty('visibility');
      el.style.removeProperty('pointer-events');
      el.style.removeProperty('transition');

      if (activeElement) {
        activeElement.style.removeProperty('transition');
      }

      // Adjust arrow position after popover is visible
      if (adjustArrow) {
        // Use requestAnimationFrame for smoother positioning
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            try {
              adjustArrow();
            } catch {}
          });
        });
      }
    } catch {
      // ignore
    }
  };

  // Function to activate a tab if needed
  const activateTab = async (tabId: string): Promise<boolean> => {
    if (typeof window === 'undefined') return false;

    try {
      // Add '#' prefix if not present
      const tabSelector = tabId.startsWith('#') || tabId.startsWith('.') ? tabId : `#${tabId}`;
      const tabElement = document.querySelector(tabSelector);

      if (!tabElement) {
        // eslint-disable-next-line no-console
        console.warn(`Tab element not found: ${tabSelector}`);
        return false;
      }

      // Try to click the tab to activate it
      if (tabElement instanceof HTMLElement) {
        // First, scroll the tab into view (important for mobile horizontal scroll)
        // tabElement.scrollIntoView({
        //   behavior: 'smooth',
        //   block: 'nearest',
        //   inline: 'center',
        // });

        // Wait a bit for scroll to complete
        await new Promise((resolve) => setTimeout(resolve, 100));

        // Set a flag to indicate this click is from the tour
        // This prevents URL changes that would reload the page
        (window as any).__tourTabClick = true;
        tabElement.click();

        // Reset the flag after a short delay
        await new Promise((resolve) => setTimeout(resolve, 100));
        (window as any).__tourTabClick = false;

        return true;
      }
    } catch {
      // ignore
    }

    return false;
  };

  // Function to find the first visible element among all matching elements
  // For elements with tabId, we always create a placeholder that will later be
  // redirected to the real element once the tab content is activated.
  const findVisibleElement = (selector: string, hasTabId: boolean = false): Element | null => {
    if (typeof window === 'undefined') return null;

    // For elements that are inside a tab (hasTabId === true), we always create a placeholder so that driver.js can focus on it before rendering the tab content.
    // Later in onHighlightStarted we will connect this placeholder to the actual element.
    if (hasTabId) {
      const placeholder = document.createElement('div');
      placeholder.id = `tour-placeholder-${selector.replace(/[^a-zA-Z0-9]/g, '')}-${Date.now()}`;
      placeholder.setAttribute('data-tour-placeholder', selector);
      // Position at center of viewport so popover shows in center
      // Use 1x1 size to minimize visual impact
      placeholder.style.cssText = `
        position: fixed;
        top: 50%;
        left: 50%;
        width: 1px;
        height: 1px;
        transform: translate(-50%, -50%);
        opacity: 0;
        pointer-events: none;
        z-index: -1;
      `;
      document.body.appendChild(placeholder);
      return placeholder;
    }
    //For steps that don't have a tabId
    // Use querySelectorAll to get ALL matching elements
    const elements = document.querySelectorAll(selector);

    if (elements.length === 0) return null;

    // If element has a tabId, return the first element even if not visible
    // because it will become visible when the tour activates its tab
    if (hasTabId && elements.length > 0) {
      return elements[0];
    }

    // Find the first visible element
    // Helper to detect loading/skeleton placeholders so we avoid anchoring tour to them
    const isLoadingElement = (el: Element) => {
      try {
        if (!(el instanceof HTMLElement)) return false;
        const cls = (el.className || '').toString();
        const loadingRegex = /(loading|spinner|skeleton|shimmer|placeholder|is-loading)/i;
        if (loadingRegex.test(cls)) return true;
        // aria-busy or data-loading attributes
        if (el.getAttribute('aria-busy') === 'true') return true;
        if (el.getAttribute('data-loading') === 'true') return true;
        // descendant spinner
        if (
          el.querySelector &&
          el.querySelector('.spinner, .loading, .skeleton, [data-loading]')
        )
          return true;
      } catch {
        // ignore
      }
      return false;
    };

    for (let i = 0; i < elements.length; i++) {
      const element = elements[i] as Element;
      const style = window.getComputedStyle(element);
      const rect = element.getBoundingClientRect();

      // Check if element is visible (not hidden by CSS and has dimensions)
      const isVisible =
        style.display !== 'none' &&
        style.visibility !== 'hidden' &&
        style.opacity !== '0' &&
        rect.width > 0 &&
        rect.height > 0;

      if (!isVisible) continue;

      // Avoid anchoring to obvious loading placeholders. If element is loading, try next match.
      if (isLoadingElement(element)) continue;

      return element;
    }

    return null;
  };

  useEffect(() => {
    if (!condition || !steps?.length) return;

    // Lock scrolling when tour starts
    lockScroll();

    // Add global CSS to control popover visibility during element preparation only
    const styleId = 'tour-transition-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        /* Hide popover only during initial element preparation (first step) */
        body.tour-initializing .driver-popover {
          opacity: 0 !important;
          visibility: hidden !important;
          pointer-events: none !important;
        }
        
        /* Temporarily hide during transitions between steps - hide popover and highlight only */
        body.tour-hiding .driver-popover {
          opacity: 0 !important;
          pointer-events: none !important;
          transition: none !important;
        }
        
        body.tour-hiding .driver-active-element {
          outline: none !important;
          transition: none !important;
        }
        
        /* Use GPU acceleration for smoother transitions */
        .driver-popover {
          will-change: opacity, transform;
          transform: translateZ(0);
        }
        
        /* Optimize highlight animations */
        .driver-active-element {
          will-change: auto;
          transform: translateZ(0);
        }
      `;
      document.head.appendChild(style);
    }

    // Add initializing class to hide popover on first step
    document.body.classList.add('tour-initializing');

    // Smart arrow positioning - aligns arrow to element position relative to popover
    const adjustArrowPosition = () => {
      if (typeof window === 'undefined') return;

      const isMobile = window.innerWidth <= 768;
      if (!isMobile) return; // Only adjust on mobile

      const popover = document.querySelector('.driver-popover') as HTMLElement;
      const arrow = document.querySelector('.driver-popover-arrow') as HTMLElement;
      const highlightedElement = document.querySelector(
        '.driver-active-element',
      ) as HTMLElement;

      if (!popover || !arrow || !highlightedElement) return;

      const popoverRect = popover.getBoundingClientRect();
      const elementRect = highlightedElement.getBoundingClientRect();

      // Calculate the center of the highlighted element
      const elementCenterX = elementRect.left + elementRect.width / 2;

      // Calculate the center of the popover
      const popoverCenterX = popoverRect.left + popoverRect.width / 2;

      // Calculate where the arrow should point (element center relative to popover)
      const arrowPosition = elementCenterX - popoverRect.left;

      // Fine tune popover position for steps with tabId to prevent overlap
      const activeIndex = driverRef.current?.getActiveIndex();
      if (activeIndex !== undefined && activeIndex >= 0) {
        const currentStep = processedSteps[activeIndex];
        const originalStep = currentStep ? steps[currentStep.originalIndex] : null;
        const hasTabId = originalStep?.tabId;
        const currentTop = parseInt(popover.style.top || '0');
        if (hasTabId) {
          // Adjust popover position if step has tabId

          if (currentTop > 0 && popoverRect.top < elementRect.top) {
            // popover is above element
            popover.style.top = `${currentTop - 12}px`;
          } else if (currentTop > 0) {
            // popover is below element
            popover.style.top = `${currentTop + 12}px`;
          }
        }
      }

      // Clamp the position to stay within popover bounds (with 20px padding)
      const minPos = 20;
      const maxPos = popoverRect.width - 20;
      const clampedPosition = Math.max(minPos, Math.min(maxPos, arrowPosition));

      // Apply the position
      arrow.style.left = `${clampedPosition}px`;
      arrow.style.right = 'auto';
    };

    // (no isReloading flag needed)
    let isFirstStep = true; // Track if this is the first step
    let reloadFallbackTimer: number | null = null;
    let layoutShiftCheckInterval: NodeJS.Timeout | null = null;

    // Helper to wait for driver popover element to be added to DOM
    const waitForPopoverElement = async (timeout = 1000): Promise<Element | null> => {
      if (typeof document === 'undefined') return null;
      const start = Date.now();
      let pop = document.querySelector('.driver-popover');
      while (!pop && Date.now() - start < timeout) {
        // small delay
        await new Promise((r) => setTimeout(r, 50));
        pop = document.querySelector('.driver-popover');
      }
      return pop;
    };

    // Process steps: find visible elements and add unique identifiers
    const processedSteps: Array<
      TourStep & { visibleElement: Element; originalIndex: number }
    > = [];

    steps.forEach((step, index) => {
      // Don't activate tabs here - they will be activated when tour reaches each step
      // in the onHighlightStarted callback below
      const visibleElement = findVisibleElement(step.element, !!step.tabId);

      if (visibleElement) {
        const targetElement = visibleElement as HTMLElement;

        // Add a unique data attribute to ensure we target the correct element
        const uniqueAttr = `data-tour-target-${Date.now()}-${index}`;
        targetElement.setAttribute(uniqueAttr, 'true');

        // Update the selector to target this specific element
        const updatedStep = {
          ...step,
          element: `[${uniqueAttr}="true"]`,
          visibleElement: targetElement,
          originalIndex: index, // Store original index to map back to original steps array
        };

        processedSteps.push(updatedStep);
      }
    });

    // Don't start tour if no visible steps
    if (processedSteps.length === 0) {
      return;
    }

    let driverObj: ReturnType<typeof driver> | null = null;

    // Helper to restore sticky positions for all tour elements
    // const restoreStickyPositions = () => {
    //   processedSteps.forEach((step) => {
    //     if (step.visibleElement) {
    //       const element = step.visibleElement as HTMLElement;
    //       if (element.hasAttribute('data-tour-original-position-inline')) {
    //         const originalPosition = element.getAttribute(
    //           'data-tour-original-position-inline',
    //         );
    //         const originalTop = element.getAttribute('data-tour-original-top-inline');
    //         const originalBottom = element.getAttribute('data-tour-original-bottom-inline');

    //         // Remove the important flag and restore original inline styles
    //         element.style.removeProperty('position');
    //         element.style.removeProperty('top');
    //         element.style.removeProperty('bottom');

    //         // Restore original inline styles (empty string means no inline style)
    //         if (originalPosition) {
    //           element.style.position = originalPosition;
    //         }
    //         if (originalTop) {
    //           element.style.top = originalTop;
    //         }
    //         if (originalBottom) {
    //           element.style.bottom = originalBottom;
    //         }

    //         // Clean up data attributes
    //         element.removeAttribute('data-tour-original-position-inline');
    //         element.removeAttribute('data-tour-original-top-inline');
    //         element.removeAttribute('data-tour-original-bottom-inline');
    //       }
    //     }
    //   });
    // };

    const onDestroyStarted = () => {
      try {
        // Clean up CSS classes
        document.body.classList.remove('tour-hiding');
        document.body.classList.remove('tour-initializing');

        // Unlock scrolling when tour ends
        unlockScroll();

        const activeIndex = driverObj?.getActiveIndex();
        const isLastStep =
          typeof activeIndex === 'number' && activeIndex === processedSteps.length - 1;

        if (isLastStep && id) {
          try {
            mutate(id, {
              onError: () => {},
            });
          } catch {}
        }

        if (isLastStep && onComplete) onComplete();

        // Always call onDestroy when tour is destroyed (completed or closed)
        if (onDestroy) onDestroy();

        // cleanup attributes/placeholders
        processedSteps.forEach((step) => {
          const attrName = step.element.match(/\[(.*?)="/)?.[1];
          if (attrName && step.visibleElement) {
            try {
              step.visibleElement.removeAttribute(attrName);
            } catch {}
            if (step.visibleElement.id?.startsWith('tour-placeholder-'))
              step.visibleElement.remove();
          }
        });

        // Restore sticky positions
        // restoreStickyPositions();

        try {
          driverObj?.destroy();
        } catch {}
      } catch {
        // ignore
      }
    };

    const onDeselected = (element?: Element | null) => {
      // Hide popover during transition between steps to prevent flash
      document.body.classList.add('tour-hiding');

      // Reset popover and highlight animation state to prevent jump when moving between steps
      // Note: We don't hide overlay - it should stay visible throughout the tour
      try {
        const popover = document.querySelector('.driver-popover') as HTMLElement;
        const activeElement = document.querySelector('.driver-active-element') as HTMLElement;

        if (popover) {
          // Force animation reset for popover
          popover.style.transition = 'none';
          popover.style.opacity = '0';

          // Force reflow to apply changes immediately
          popover.offsetHeight;
        }

        if (activeElement) {
          // Force animation reset for highlighted element
          activeElement.style.transition = 'none';

          // Force reflow
          activeElement.offsetHeight;
        }
      } catch {
        // ignore
      }

      // For tab steps, reset them back to a "placeholder" state when we leave them
      // so that the next time we enter that step, it follows the exact same flow
      // as the very first time (placeholder -> activateTab -> real element).
      try {
        const deselectedEl = element as HTMLElement | null;

        if (deselectedEl) {
          const stepIndex = processedSteps.findIndex((s) => s.visibleElement === deselectedEl);

          if (stepIndex >= 0) {
            const step = processedSteps[stepIndex];
            const originalStep = steps[step.originalIndex];

            if (originalStep?.tabId && originalStep.element) {
              // Recreate a fresh placeholder centered in the viewport
              const originalSelector =
                originalStep.element.startsWith('#') || originalStep.element.startsWith('.')
                  ? originalStep.element
                  : `#${originalStep.element}`;

              const placeholder = findVisibleElement(originalSelector, true);

              // Move the unique tour attribute back from the real element to the new placeholder
              const attrName = step.element.match(/\[(.*?)="/)?.[1];

              if (placeholder && attrName) {
                if (step.visibleElement && step.visibleElement !== placeholder) {
                  try {
                    (step.visibleElement as HTMLElement).removeAttribute(attrName);
                  } catch {}
                }

                try {
                  (placeholder as HTMLElement).setAttribute(attrName, 'true');
                } catch {}

                // Update step to point back to the placeholder,
                // exactly like in the initial processing phase
                step.visibleElement = placeholder;
                step.element = `[${attrName}="true"]`;
              }
            }
          }
        }
      } catch {
        // ignore
      }

      // Don't remove placeholders here - they need to persist for future tab steps
      // Placeholders are cleaned up individually when replaced (line ~440) and
      // collectively when tour ends (in onDestroyStarted)
    };

    // Helper to check if element or any parent is fixed/sticky
    const hasFixedOrStickyParent = (
      element: HTMLElement,
    ): { isFixed: boolean; isSticky: boolean; fixedParent: HTMLElement | null } => {
      let current: HTMLElement | null = element;
      let hasFixed = false;
      let hasSticky = false;
      let fixedParentElement: HTMLElement | null = null;

      while (current && current !== document.body) {
        const computedStyle = window.getComputedStyle(current);
        const position = computedStyle.position;

        if (position === 'fixed') {
          hasFixed = true;
          if (!fixedParentElement) fixedParentElement = current;
        }
        if (position === 'sticky' || position === '-webkit-sticky') {
          hasSticky = true;
        }

        current = current.parentElement;
      }

      return { isFixed: hasFixed, isSticky: hasSticky, fixedParent: fixedParentElement };
    };

    // Helper to check if element or any parent was disabled (has tour attribute)
    const hasDisabledParent = (element: HTMLElement): boolean => {
      let current: HTMLElement | null = element;

      while (current && current !== document.body) {
        if (current.hasAttribute('data-tour-original-position-inline')) {
          return true;
        }
        current = current.parentElement;
      }

      return false;
    };

    // Shared visibility helper used in several places
    const isElementVisibleStrict = (el: HTMLElement | null): boolean => {
      if (!el) return false;
      try {
        const style = window.getComputedStyle(el);
        const rect = el.getBoundingClientRect();
        return (
          style.display !== 'none' &&
          style.visibility !== 'hidden' &&
          style.opacity !== '0' &&
          rect.width > 0 &&
          rect.height > 0
        );
      } catch {
        return false;
      }
    };

    // Helper function to scroll element into viewport with proper spacing for popover
    const scrollToCenter = async (element: HTMLElement, isTabElement: boolean = false) => {
      try {
        const isMobile = window.innerWidth <= 768;
        const minTopOffset = isMobile ? 80 : 100; // Minimum distance from top of viewport
        const minBottomOffset = isMobile ? 220 : 100; // Minimum distance from bottom

        // Check if element or any parent is sticky or fixed
        const computedStyle = window.getComputedStyle(element);
        const position = computedStyle.position;
        const elementIsSticky = position === 'sticky' || position === '-webkit-sticky';
        const elementIsFixed = position === 'fixed';

        // Also check parents
        const parentCheck = hasFixedOrStickyParent(element);
        const isSticky = elementIsSticky || parentCheck.isSticky;
        const isFixed = elementIsFixed || parentCheck.isFixed;
        const fixedParent = parentCheck.fixedParent;

        // If element is fixed or sticky, don't scroll - it's already in view
        // if (isFixed) {
        //   // Fixed elements are always in viewport, no need to scroll
        //   return;
        // }

        if (isMobile) {
          // Check if element or parent was already disabled by tour
          const alreadyDisabled = hasDisabledParent(element);

          // If already disabled (fixed/sticky parent), don't scroll again
          if (alreadyDisabled) {
            return; // Skip scrolling for elements that were already processed
          }

          // For sticky elements, we need to find their natural position in the DOM
          // Then scroll to center them (which will unstick them)
          let absoluteElementTop: number;
          let elementHeight: number;
          const viewportHeight = window.innerHeight;

          if (isSticky || isFixed) {
            // Determine which element to disable (element itself or fixed parent)
            const elementToDisable = fixedParent || element;

            // Disable sticky/fixed for the duration of the tour
            // Store original values to restore later (only once per element)
            if (!elementToDisable.hasAttribute('data-tour-original-position-inline')) {
              // Store inline style values (to restore exactly what was there)
              elementToDisable.setAttribute(
                'data-tour-original-position-inline',
                elementToDisable.style.position || '',
              );
              elementToDisable.setAttribute(
                'data-tour-original-top-inline',
                elementToDisable.style.top || '',
              );
              elementToDisable.setAttribute(
                'data-tour-original-bottom-inline',
                elementToDisable.style.bottom || '',
              );

              // Disable sticky/fixed permanently (for tour duration)
              elementToDisable.style.setProperty('position', 'static', 'important');

              // Force a reflow to get the actual position
              element.offsetHeight; // Force reflow
            }

            // For elements with fixed parent, don't scroll - just disable parent and return
            // Driver.js will handle positioning the popover correctly
            if (fixedParent) {
              return; // Skip scrolling for elements inside fixed containers
            }

            const naturalRect = element.getBoundingClientRect();
            absoluteElementTop = window.pageYOffset + naturalRect.top;
            elementHeight = naturalRect.height;
          } else {
            // For non-sticky elements, get position normally
            const elementRect = element.getBoundingClientRect();
            absoluteElementTop = window.pageYOffset + elementRect.top;
            elementHeight = elementRect.height;
          }

          // Position element exactly in the center (50% of viewport)
          const targetPosition = viewportHeight / 2;
          const scrollToPosition = absoluteElementTop - targetPosition + elementHeight / 2;

          // Use direct DOM manipulation for more reliable scrolling
          const scrollTop = Math.max(0, scrollToPosition);

          // Try multiple methods to ensure scrolling works
          window.scrollTo({
            top: scrollTop,
            behavior: 'auto',
          });

          // Fallback: direct property assignment
          document.documentElement.scrollTop = scrollTop;
          document.body.scrollTop = scrollTop;

          await new Promise((r) => setTimeout(r, 100));

          // Verify scroll happened with multiple retries
          for (let retry = 0; retry < 3; retry++) {
            const currentScroll = window.pageYOffset || document.documentElement.scrollTop;
            const scrollDiff = Math.abs(currentScroll - scrollTop);

            if (scrollDiff > 30) {
              // Force scroll again
              window.scrollTo({
                top: scrollTop,
                behavior: 'auto',
              });
              document.documentElement.scrollTop = scrollTop;
              document.body.scrollTop = scrollTop;
              await new Promise((r) => setTimeout(r, 50));
            } else {
              // Scroll is accurate enough
              break;
            }
          }
        } else {
          // For desktop, use scrollIntoView with center alignment
          element.scrollIntoView({
            behavior: 'auto',
            block: 'center',
            inline: 'nearest',
          });

          await new Promise((r) => setTimeout(r, 50));
        }

        // For desktop only, verify element is in viewport and adjust if needed
        if (!isMobile) {
          const elementRect = element.getBoundingClientRect();
          const viewportHeight = window.innerHeight;

          // Adjust if too close to top
          if (elementRect.top < minTopOffset) {
            const scrollAdjustment = minTopOffset - elementRect.top;
            const currentScroll = window.pageYOffset || window.scrollY;

            const newScrollTop = currentScroll - scrollAdjustment;
            document.documentElement.scrollTop = newScrollTop;
            document.body.scrollTop = newScrollTop;

            await new Promise((r) => setTimeout(r, 25));
          }

          // Adjust if too close to bottom
          if (elementRect.bottom > viewportHeight - minBottomOffset) {
            const scrollAdjustment = elementRect.bottom - (viewportHeight - minBottomOffset);
            const currentScroll = window.pageYOffset || window.scrollY;

            const newScrollTop = currentScroll + scrollAdjustment;
            document.documentElement.scrollTop = newScrollTop;
            document.body.scrollTop = newScrollTop;

            await new Promise((r) => setTimeout(r, 25));
          }
        }
      } catch (e) {
        // Ignore scroll errors
      }
    };

    // onHighlightStarted: handle highlight events from driver.js. We use a reloadSet to
    // coordinate placeholder -> real element swaps for tab steps so we don't flicker or jump.
    const reloadSet = new Set<number>();
    let handlingStep = false;

    const onHighlightStarted = async () => {
      if (handlingStep) return; // guard re-entrancy
      handlingStep = true;
      try {
        // Get current active index immediately
        const activeIndex = driverObj?.getActiveIndex();
        if (activeIndex === undefined || activeIndex < 0) return;

        // Check if this is the first step
        const isCurrentStepFirst = activeIndex === 0;

        // Check if current step has tabId - if yes, hide popover immediately to prevent flash
        const currentStep = processedSteps[activeIndex];
        const originalStep = currentStep ? steps[currentStep.originalIndex] : null;
        const hasTabId = originalStep?.tabId;

        if (hasTabId) {
          // Immediately hide popover for tab steps to prevent flash of previous step
          document.body.classList.add('tour-hiding');
        }

        // For first step only, hide popover to prevent flash during initial positioning
        if (isCurrentStepFirst && isFirstStep) {
          document.body.classList.add('tour-initializing');
        }

        // If this is a reload highlight after we swapped placeholder -> real element
        if (reloadSet.has(activeIndex)) {
          reloadSet.delete(activeIndex);
          // Clear any fallback timer
          try {
            if (reloadFallbackTimer) {
              clearTimeout(reloadFallbackTimer);
              reloadFallbackTimer = null;
            }
          } catch {}

          // Wait for driver.js to attach to real element
          await new Promise((r) => setTimeout(r, 100));

          // Force update events to help driver.js sync with layout
          if (window.innerWidth <= 768) {
            window.dispatchEvent(new Event('resize'));
            window.dispatchEvent(new Event('scroll'));
          }

          // Multiple refreshes for accurate positioning before showing
          try {
            driverObj?.refresh();
          } catch {}

          await new Promise((r) => setTimeout(r, 100));

          try {
            driverObj?.refresh();
          } catch {}

          await new Promise((r) => setTimeout(r, 100));

          // Final refresh for precise positioning
          try {
            driverObj?.refresh();
          } catch {}

          await new Promise((r) => setTimeout(r, 75));

          // Remove all hiding classes and show the popover - let driver.js animation handle the fade-in
          document.body.classList.remove('tour-hiding');
          document.body.classList.remove('tour-initializing');

          // Small delay to ensure popover is positioned before becoming visible
          await new Promise((r) => setTimeout(r, 50));

          const pop = await waitForPopoverElement(1000);
          if (pop) {
            showPopover(pop, adjustArrowPosition);
          }
          if (isCurrentStepFirst) isFirstStep = false;
          return;
        }

        // Normal highlight processing for the active step
        const currentIndex = activeIndex;
        // currentStep and originalStep already defined above
        // Use originalIndex to get the correct element from original steps array
        // This handles cases where some steps are filtered out (e.g., on mobile)
        const originalElement = currentStep ? steps[currentStep.originalIndex]?.element : null;

        // ensure there's a popover element reference (may be created asynchronously)
        let popover =
          document.querySelector('.driver-popover') || (await waitForPopoverElement(1000));

        if (!originalElement) {
          document.body.classList.remove('tour-hiding');
          document.body.classList.remove('tour-initializing');
          if (popover) showPopover(popover, adjustArrowPosition);
          if (isCurrentStepFirst) isFirstStep = false;
          return;
        }

        const elementSelector =
          originalElement.startsWith('#') || originalElement.startsWith('.')
            ? originalElement
            : `#${originalElement}`;

        // Check if visibleElement still exists in DOM (important when going back from tab steps)
        // If element doesn't exist or is a placeholder, find the real element again
        const uniqueAttr = currentStep.element.match(/\[(.*?)="/)?.[1];

        const needsElementRefresh =
          !currentStep.visibleElement ||
          !document.body.contains(currentStep.visibleElement) ||
          currentStep.visibleElement.id?.startsWith('tour-placeholder-') ||
          !isElementVisibleStrict(currentStep.visibleElement as HTMLElement);

        if (needsElementRefresh && uniqueAttr) {
          // Find the real element again - try multiple selectors with retry
          let realElement: HTMLElement | null = null;

          // Try up to 5 times with delays to ensure element is available
          for (let attempt = 0; attempt < 5 && !realElement; attempt++) {
            if (attempt > 0) {
              await new Promise((r) => setTimeout(r, 100));
            }

            const elements = document.querySelectorAll<HTMLElement>(elementSelector);
            for (let i = 0; i < elements.length; i++) {
              const el = elements[i];
              if (isElementVisibleStrict(el)) {
                realElement = el as HTMLElement;
                break;
              }
            }
          }

          if (realElement) {
            // Clean up old element
            try {
              if (currentStep.visibleElement) {
                currentStep.visibleElement.removeAttribute(uniqueAttr);
                if (currentStep.visibleElement.id?.startsWith('tour-placeholder-')) {
                  currentStep.visibleElement.remove();
                }
              }
            } catch {}

            // Update to new element
            try {
              realElement.setAttribute(uniqueAttr, 'true');
              currentStep.visibleElement = realElement;
            } catch {}

            // Wait a bit for element to be ready
            await new Promise((r) => setTimeout(r, 50));

            // Force driver to re-evaluate the element
            try {
              driverObj?.refresh();
              await new Promise((r) => setTimeout(r, 25));
            } catch {}
          }
        }

        // If step belongs to a tab, activate tab and wait for the real element
        if (currentStep?.tabId) {
          // Hide popover during tab activation and scrolling
          // For first step, we already have tour-initializing, so hiding is redundant but doesn't hurt
          if (!document.body.classList.contains('tour-initializing')) {
            document.body.classList.add('tour-hiding');
          }

          await activateTab(currentStep.tabId);

          // Wait for tab content to render (retry loop)
          let realElement: HTMLElement | null = null;
          const start = Date.now();
          const maxWait = 2500;
          while (Date.now() - start < maxWait) {
            realElement = document.querySelector(elementSelector) as HTMLElement | null;
            if (realElement) break;
            await new Promise((r) => setTimeout(r, 100));
          }

          if (!realElement) {
            // couldn't find real element; just show popover to avoid leaving user stuck
            document.body.classList.remove('tour-hiding');
            document.body.classList.remove('tour-initializing');
            if (popover) showPopover(popover, adjustArrowPosition);
            if (isCurrentStepFirst) isFirstStep = false;
            return;
          }

          // Transfer unique attribute to the real element (works for both placeholder and when going back)
          const uniqueAttr = currentStep.element.match(/\[(.*?)="/)?.[1];
          if (uniqueAttr) {
            // Check if current visibleElement is a placeholder
            const isPlaceholder =
              currentStep.visibleElement?.hasAttribute('data-tour-placeholder');

            // If it's a placeholder or the real element is not in DOM (when going back), update it
            if (isPlaceholder || !document.body.contains(currentStep.visibleElement)) {
              const oldElement = currentStep.visibleElement as HTMLElement;
              try {
                // Remove attribute from old element
                oldElement?.removeAttribute(uniqueAttr);
              } catch {}
              try {
                // Add attribute to new real element
                realElement.setAttribute(uniqueAttr, 'true');
              } catch {}
              // Update the reference
              currentStep.visibleElement = realElement;
              currentStep.element = `[${uniqueAttr}="true"]`;
              try {
                // Clean up placeholder if exists
                if (oldElement?.id?.startsWith('tour-placeholder-')) oldElement.remove();
              } catch {}
            }
          }

          // Scroll tab element to viewport (with extra offset for tab elements)
          await scrollToCenter(realElement, true);

          // Wait for scroll to complete
          await new Promise((r) => setTimeout(r, 100));

          // Verify element is centered (for all tab steps on mobile) - multiple attempts
          const isMobile = window.innerWidth <= 768;
          if (isMobile) {
            // Check if element or parent was originally fixed/sticky and disabled
            const wasStickyDisabled = hasDisabledParent(realElement);

            // Skip verification for elements that were fixed/sticky
            // (they are now static and positioned correctly, no need to verify)
            if (!wasStickyDisabled) {
              // Try up to 3 times to center the element properly
              for (let attempt = 0; attempt < 3; attempt++) {
                const elementRect = realElement.getBoundingClientRect();
                const viewportHeight = window.innerHeight;
                const elementCenter = elementRect.top + elementRect.height / 2;
                const viewportCenter = viewportHeight / 2;
                const offset = Math.abs(elementCenter - viewportCenter);

                // If not centered enough (more than 50px off), scroll again
                if (offset > 50) {
                  await scrollToCenter(realElement, true);
                  await new Promise((r) => setTimeout(r, 75));
                } else {
                  // Successfully centered, break the loop
                  break;
                }
              }
            }
          }

          // Let driver recalculate and then ask it to moveTo the same index so overlay attaches to real element
          reloadSet.add(currentIndex);

          // Multiple refreshes for accurate positioning in mobile
          try {
            driverObj?.refresh();
          } catch {}

          await new Promise((r) => setTimeout(r, 50));

          try {
            driverObj?.refresh();
          } catch {}

          await new Promise((r) => setTimeout(r, 50));

          try {
            driverObj?.moveTo(currentIndex);
          } catch {}

          // Wait for moveTo to complete and recalculate position
          await new Promise((r) => setTimeout(r, 100));

          // Force more refreshes for mobile tab elements (isMobile already defined above)
          if (isMobile) {
            try {
              driverObj?.refresh();
            } catch {}

            await new Promise((r) => setTimeout(r, 50));

            try {
              driverObj?.refresh();
            } catch {}

            await new Promise((r) => setTimeout(r, 50));
          }

          // Fallback timeout
          try {
            if (reloadFallbackTimer) {
              clearTimeout(reloadFallbackTimer);
              reloadFallbackTimer = null;
            }
            const idx = currentIndex;
            const fallbackDelay = 400; // Increased for more reliable positioning
            reloadFallbackTimer = window.setTimeout(async () => {
              if (!reloadSet.has(idx)) return;
              reloadSet.delete(idx);
              document.body.classList.remove('tour-hiding');
              document.body.classList.remove('tour-initializing');
              const p = await waitForPopoverElement(1000);
              if (p) showPopover(p, adjustArrowPosition);
            }, fallbackDelay) as unknown as number;
          } catch {}

          if (isCurrentStepFirst) isFirstStep = false;
          return;
        }

        // Non-tab steps: ensure target in viewport, refresh and show popover
        let targetElement = document.querySelector(elementSelector) as HTMLElement | null;

        // If element not found, try using the unique attribute selector
        if (!targetElement && uniqueAttr) {
          targetElement = document.querySelector(
            `[${uniqueAttr}="true"]`,
          ) as HTMLElement | null;
        }

        // If element found but not visible, or not found at all, try finding it again
        if (
          (!targetElement || !isElementVisibleStrict(targetElement)) &&
          !originalStep?.tabId
        ) {
          const originalSelector =
            originalElement.startsWith('#') || originalElement.startsWith('.')
              ? originalElement
              : `#${originalElement}`;

          // Try to find visible element (like we do at initialization)
          const elements = document.querySelectorAll<HTMLElement>(originalSelector);
          let foundVisible = false;

          for (let i = 0; i < elements.length; i++) {
            const element = elements[i] as HTMLElement;
            if (isElementVisibleStrict(element)) {
              targetElement = element;
              foundVisible = true;
              break;
            }
          }

          // If still not found, retry up to 3 times with small delays (element might be loading)
          if (!foundVisible) {
            for (let retry = 0; retry < 3; retry++) {
              await new Promise((r) => setTimeout(r, 50));
              const retryElements = document.querySelectorAll<HTMLElement>(originalSelector);
              for (let i = 0; i < retryElements.length; i++) {
                const element = retryElements[i];
                if (isElementVisibleStrict(element)) {
                  targetElement = element;
                  foundVisible = true;
                  break;
                }
              }
              if (foundVisible) break;
            }
          }

          // If found now, update the attribute
          if (targetElement && uniqueAttr) {
            try {
              targetElement.setAttribute(uniqueAttr, 'true');
              currentStep.visibleElement = targetElement;

              // Wait for element to be ready
              await new Promise((r) => setTimeout(r, 50));

              // Force driver to re-evaluate the element
              try {
                driverObj?.refresh();
                await new Promise((r) => setTimeout(r, 25));
              } catch {}
            } catch {}
          }
        }

        if (!targetElement) {
          // Element not found - try forcing a moveTo to current index to retry
          try {
            driverObj?.refresh();
            await new Promise((r) => setTimeout(r, 50));
            driverObj?.moveTo(currentIndex);
            await new Promise((r) => setTimeout(r, 100));
          } catch {}

          // Remove all hiding classes and show popover anyway to prevent user from getting stuck
          document.body.classList.remove('tour-hiding');
          document.body.classList.remove('tour-initializing');
          const finalPopover = document.querySelector('.driver-popover') || popover;
          if (finalPopover) {
            showPopover(finalPopover, adjustArrowPosition);
          }
          if (isCurrentStepFirst) isFirstStep = false;
          return;
        }

        // Element found - proceed with positioning

        // Check if element needs special handling (sticky/fixed on mobile)
        const isMobileDevice = window.innerWidth <= 768;
        let needsManualScroll = false;

        if (isMobileDevice) {
          const parentCheck = hasFixedOrStickyParent(targetElement);
          const computedStyle = window.getComputedStyle(targetElement);
          const position = computedStyle.position;
          const elementIsSticky = position === 'sticky' || position === '-webkit-sticky';
          const elementIsFixed = position === 'fixed';

          const isSticky = elementIsSticky || parentCheck.isSticky;
          const isFixed = elementIsFixed || parentCheck.isFixed;
          const fixedParent = parentCheck.fixedParent;

          // Check if NOT already disabled
          const alreadyDisabled = hasDisabledParent(targetElement);

          if ((isSticky || isFixed) && !alreadyDisabled) {
            needsManualScroll = true; // Need manual scroll for sticky/fixed elements
            const elementToDisable = fixedParent || targetElement;

            // Store original values and disable ONLY if not already done
            if (!elementToDisable.hasAttribute('data-tour-original-position-inline')) {
              elementToDisable.setAttribute(
                'data-tour-original-position-inline',
                elementToDisable.style.position || '',
              );
              elementToDisable.setAttribute(
                'data-tour-original-top-inline',
                elementToDisable.style.top || '',
              );
              elementToDisable.setAttribute(
                'data-tour-original-bottom-inline',
                elementToDisable.style.bottom || '',
              );

              // Disable sticky/fixed
              elementToDisable.style.setProperty('position', 'static', 'important');

              // Force reflow to ensure layout is stable
              targetElement.offsetHeight;

              // Wait for layout to settle
              await new Promise((r) => setTimeout(r, 75));
            }
          }
        }

        // Only scroll manually if needed (sticky/fixed elements on mobile)
        if (needsManualScroll) {
          await scrollToCenter(targetElement, false);
          await new Promise((r) => setTimeout(r, 75));

          // Verify element is centered - single attempt
          const elementRect = targetElement.getBoundingClientRect();
          const viewportHeight = window.innerHeight;
          const elementCenter = elementRect.top + elementRect.height / 2;
          const viewportCenter = viewportHeight / 2;
          const offset = Math.abs(elementCenter - viewportCenter);

          // If not centered enough, scroll once more
          if (offset > 50) {
            await scrollToCenter(targetElement, false);
            await new Promise((r) => setTimeout(r, 50));
          }
        }

        // Refresh to let driver.js know about final position
        try {
          driverObj?.refresh();
        } catch {}

        await new Promise((r) => setTimeout(r, 50));

        // For non-tab steps, remove hiding immediately and let driver.js animate naturally
        document.body.classList.remove('tour-hiding');
        document.body.classList.remove('tour-initializing');

        const finalPopover = document.querySelector('.driver-popover') || popover;
        if (finalPopover) {
          showPopover(finalPopover, adjustArrowPosition);
        }
        if (isCurrentStepFirst) isFirstStep = false;
      } finally {
        handlingStep = false;
      }
    };

    // Initialize the tour driver with full popover config so styling and onPopoverRender run
    try {
      const stepsForDriver = processedSteps.map((s, index) => {
        const isLastStep = index === processedSteps.length - 1;
        // Get original step data to ensure we have correct icon/title even if steps were filtered
        const originalStep = steps[s.originalIndex];
        const iconData = originalStep?.icon || s.icon;
        const titleText = originalStep?.title || s.title;

        // Add tab-specific class for styling hook
        const popoverClass = originalStep.tabId ? 'driver-popover-tab-element' : '';

        // Handle icon - can be string URL or object with url/id
        let iconUrl = '';
        if (typeof iconData === 'string') {
          iconUrl = iconData;
        } else if (iconData && typeof iconData === 'object') {
          // If it's an object, try to get URL from common properties
          iconUrl = iconData.url || (iconData.id ? `/api/files/${iconData.id}` : '');
        }

        const titleContent = iconUrl
          ? `<div style="display: flex; align-items: center; gap: 8px;"><div style="width: 28px; height: 28px; background-color: #FFAE11; border-radius: 4px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; overflow: hidden;"><img src="${iconUrl}" alt="" style="width: calc(100% - 4px); height: calc(100% - 4px); object-fit: contain; padding: 2px; display: block;" /></div>${titleText ? `<span>${titleText}</span>` : ''}</div>`
          : titleText || '';

        const nextBtn = `
  <div style="display:flex; align-items:center; gap:3px;">
    ${productLocalization.next}
    <svg width="8" height="10" viewBox="0 0 3 5" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M2.25 4.25L0.25 2.25L2.25 0.25" stroke="currentColor" stroke-width="0.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
  </div>
`;

        return {
          element: s.element,
          popover: {
            className: popoverClass,
            title: titleContent,
            description: s.description,
            position: s.position || 'auto',
            side: s.position || 'auto',
            align: 'start',
            offset: s.position === 'right' ? 'auto' : 10,
            nextBtnText: nextBtn,
            prevBtnText: productLocalization.previous,
            doneBtnText: productLocalization.youCanBuyTheProductNow,
            closeBtnText: productLocalization.iUnderstand,
            onPopoverRender: (popover: any) => {
              try {
                const wrapper = popover.wrapper as HTMLElement;

                const isMobile = window.innerWidth <= 768;
                const originalStep = steps[s.originalIndex];

                if (isMobile && !originalStep?.tabId) {
                  wrapper.classList.add('tour-popover');
                } else {
                  wrapper.classList.remove('tour-popover');
                }
              } catch {}
              setTimeout(() => {
                try {
                  const nextBtn = popover.wrapper.querySelector('.driver-popover-next-btn');
                  const prevBtn = popover.wrapper.querySelector('.driver-popover-prev-btn');
                  const closeBtn = popover.wrapper.querySelector('.driver-popover-close-btn');
                  const popoverTitle = popover.wrapper.querySelector('.driver-popover-title');
                  const popoverDescription = popover.wrapper.querySelector(
                    '.driver-popover-description',
                  );

                  if (popoverTitle && popoverDescription && popoverTitle.parentNode) {
                    const wrapper = document.createElement('div');
                    wrapper.style.background =
                      'linear-gradient(180deg, #FFF5E6 10%, rgba(255, 182, 22, 0) 100%)';
                    wrapper.style.padding = '5px';
                    wrapper.style.margin = '0';
                    wrapper.style.borderRadius = '8px';
                    popoverTitle.style.margin = '0';
                    popoverTitle.style.padding = '0';
                    popoverTitle.style.marginBottom = '10px';
                    popoverDescription.style.margin = '0';
                    popoverDescription.style.padding = '0';
                    popoverTitle.parentNode.insertBefore(wrapper, popoverTitle);
                    wrapper.appendChild(popoverTitle);
                    wrapper.appendChild(popoverDescription);
                  }

                  if (nextBtn) {
                    nextBtn.id = 'tour-next-btn';
                    if (isLastStep) {
                      nextBtn.textContent = productLocalization.youCanBuyTheProductNow;
                      nextBtn.id = 'tour-done-btn';
                    }
                  }
                  const isFirstStep = index === 0;

                  if (prevBtn) {
                    prevBtn.id = 'tour-prev-btn';
                    if (isFirstStep) {
                      prevBtn.style.display = 'none';
                    } else {
                      prevBtn.style.display = '';
                    }
                  }
                  if (closeBtn) {
                    closeBtn.id = 'tour-close-btn';
                    closeBtn.textContent = productLocalization.iUnderstand;
                    closeBtn.setAttribute('aria-label', productLocalization.iUnderstand);
                  }
                } catch {}
              }, 0);
            },
          } as any,
        };
      });

      driverObj = driver({
        animate: true, // Use driver.js animations
        opacity: 0.4,
        padding: 0,
        allowClose: true,
        allowKeyboardControl: true,
        disableActiveInteraction: false,
        stagePadding: 0,
        stageRadius: 4,
        overlayClickNext: false,
        showButtons: true,
        // Optimize popover positioning speed
        popoverOffset: 10,
        smoothScroll: false, // We handle scrolling ourselves
        onDeselected,
        onHighlightStarted,
        onDestroyStarted,
        steps: stepsForDriver,
        ...options,
      });

      driverRef.current = driverObj;

      // Pre-scroll first element to center before starting tour
      // This handles layout shifts from lazy-loaded images/banners
      const preScrollFirstElement = async () => {
        if (processedSteps.length === 0) return;

        const firstStep = processedSteps[0];
        const firstElement = firstStep.visibleElement as HTMLElement;

        // Don't scroll placeholder elements
        if (!firstElement || firstElement.id?.startsWith('tour-placeholder-')) {
          return;
        }

        // Check if element has fixed/sticky parent BEFORE scrolling
        const parentCheck = hasFixedOrStickyParent(firstElement);

        // If element has fixed parent, just disable it and skip all scrolling
        if (parentCheck.isFixed && parentCheck.fixedParent) {
          const isMobile = window.innerWidth <= 768;
          if (isMobile) {
            // Disable the fixed parent ONLY if not already done
            if (!parentCheck.fixedParent.hasAttribute('data-tour-original-position-inline')) {
              parentCheck.fixedParent.setAttribute(
                'data-tour-original-position-inline',
                parentCheck.fixedParent.style.position || '',
              );
              parentCheck.fixedParent.setAttribute(
                'data-tour-original-top-inline',
                parentCheck.fixedParent.style.top || '',
              );
              parentCheck.fixedParent.setAttribute(
                'data-tour-original-bottom-inline',
                parentCheck.fixedParent.style.bottom || '',
              );

              // Only change position if we just stored the original
              parentCheck.fixedParent.style.setProperty('position', 'static', 'important');
            }
          }
          return; // Skip all scrolling for fixed parent elements
        }

        // Scroll first element to center before tour starts
        await scrollToCenter(firstElement, false);

        // Wait for potential layout shifts (images/banners loading)
        const isMobile = window.innerWidth <= 768;
        if (isMobile) {
          await new Promise((r) => setTimeout(r, 150));

          // Check if element or parent was originally fixed/sticky and disabled
          const wasStickyDisabled = hasDisabledParent(firstElement);

          // Skip verification for elements that were fixed/sticky
          // (they are now static and positioned correctly, no need to verify)
          if (!wasStickyDisabled) {
            // Try multiple times to ensure element is centered (up to 3 attempts)
            for (let attempt = 0; attempt < 3; attempt++) {
              const elementRect = firstElement.getBoundingClientRect();
              const viewportHeight = window.innerHeight;
              const elementCenter = elementRect.top + elementRect.height / 2;
              const viewportCenter = viewportHeight / 2;
              const offset = Math.abs(elementCenter - viewportCenter);

              // If element moved more than 50px from center, re-scroll
              if (offset > 50) {
                await scrollToCenter(firstElement, false);
                await new Promise((r) => setTimeout(r, 100));
              } else {
                // Successfully centered
                break;
              }
            }
          }
        } else {
          // For desktop, shorter wait
          await new Promise((r) => setTimeout(r, 50));
        }
      };

      const startTimer = setTimeout(async () => {
        try {
          // Pre-scroll first element on mobile
          await preScrollFirstElement();

          // Set up layout shift observer for first step (mobile only)
          const isMobile = window.innerWidth <= 768;
          if (isMobile && processedSteps.length > 0) {
            const firstElement = processedSteps[0].visibleElement as HTMLElement;

            // Don't set up shift observer for sticky/fixed elements (they're static now)
            const wasStickyDisabled = firstElement ? hasDisabledParent(firstElement) : false;

            if (
              firstElement &&
              !firstElement.id?.startsWith('tour-placeholder-') &&
              !wasStickyDisabled
            ) {
              let lastTop = firstElement.getBoundingClientRect().top;
              let checkCount = 0;
              const maxChecks = 10; // Check for 5 seconds (10 * 500ms)

              layoutShiftCheckInterval = setInterval(async () => {
                checkCount++;

                // Stop after max checks or when first step is done
                if (checkCount >= maxChecks || !isFirstStep) {
                  if (layoutShiftCheckInterval) {
                    clearInterval(layoutShiftCheckInterval);
                    layoutShiftCheckInterval = null;
                  }
                  return;
                }

                const currentRect = firstElement.getBoundingClientRect();
                const shift = Math.abs(currentRect.top - lastTop);

                // If element shifted more than 50px, re-center it
                if (shift > 50) {
                  const viewportHeight = window.innerHeight;
                  const elementCenter = currentRect.top + currentRect.height / 2;
                  const viewportCenter = viewportHeight / 2;

                  // Only re-scroll if not already centered
                  if (Math.abs(elementCenter - viewportCenter) > 80) {
                    await scrollToCenter(firstElement, false);

                    // Refresh driver to update overlay position
                    try {
                      driverObj?.refresh();
                    } catch {}
                  }
                }

                lastTop = currentRect.top;
              }, 250) as any;
            }
          }

          driverObj?.drive();
        } catch {}
      }, delay);

      // Initial show timer - removed as driver.js handles animation now
      // Steps will be shown by onHighlightStarted after positioning
      let showTimer: NodeJS.Timeout | null = null;

      // cleanup specific to this try block
      try {
        // nothing here
      } catch {}

      return () => {
        try {
          clearTimeout(startTimer);
        } catch {}
        try {
          if (showTimer) clearTimeout(showTimer);
        } catch {}
        try {
          if (layoutShiftCheckInterval) {
            clearInterval(layoutShiftCheckInterval);
            layoutShiftCheckInterval = null;
          }
        } catch {}
        try {
          if (reloadFallbackTimer) {
            clearTimeout(reloadFallbackTimer);
            reloadFallbackTimer = null;
          }
        } catch {}
        // Clean up CSS classes
        try {
          document.body.classList.remove('tour-hiding');
          document.body.classList.remove('tour-initializing');
        } catch {}
        // Unlock scroll on cleanup
        try {
          unlockScroll();
        } catch {}
        // Restore sticky positions
        // try {
        //   restoreStickyPositions();
        // } catch {}
      };
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[tour] driver initialization error:', e);
      // Unlock scroll on error too
      try {
        unlockScroll();
      } catch {}
      // Restore sticky positions on error
      // try {
      //   restoreStickyPositions();
      // } catch {}
      return () => {
        try {
          if (layoutShiftCheckInterval) {
            clearInterval(layoutShiftCheckInterval);
            layoutShiftCheckInterval = null;
          }
        } catch {}
        try {
          if (reloadFallbackTimer) {
            clearTimeout(reloadFallbackTimer);
            reloadFallbackTimer = null;
          }
        } catch {}
        try {
          unlockScroll();
        } catch {}
        // try {
        //   restoreStickyPositions();
        // } catch {}
      };
    }
  }, [condition, steps, options, delay, onComplete]);
}
